import * as React from 'react'
import { Mark } from '@tiptap/pm/model'
import { EditorState, PluginKey } from '@tiptap/pm/state'
import { Editor } from '@tiptap/core'
import { EditorView } from '@tiptap/pm/view'
import { Props } from 'tippy.js'
import { clsx } from 'clsx'
import { isWindows } from 'react-device-detect'

import { BaseHubInlineBlock } from '../basehub-simple-block'
import { Tooltip } from '~/sotano-kit/primitives/tooltip'
import { Link } from '../link'
import { BasehubMarkHoverPlugin } from '.'

interface BasehubActionsMenuProps {
  editor: Editor
  pluginKey?: string | PluginKey
  tippyOptions?: Partial<Props>
  updateDelay?: number
  shouldShow?:
    | ((props: {
        editor: Editor
        view: EditorView
        state: EditorState
        oldState?: EditorState
        from: number
        to: number
        hoverTarget?: HTMLElement
      }) => boolean)
    | null
  className?: string
}

export const BasehubActionsMenu: React.FC<BasehubActionsMenuProps> = ({
  editor,
  pluginKey = 'basehubActions',
  tippyOptions = {
    theme: 'translucent',
  },
  updateDelay,
  shouldShow = null,
  className,
}) => {
  const [element, setElement] = React.useState<HTMLDivElement | null>(null)
  const [_instanceId, setInstanceId] = React.useState<string | null>(null)

  // Handle hover plugin registration
  React.useEffect(() => {
    if (!element || editor.isDestroyed) {
      return
    }

    const hoverPlugin = BasehubMarkHoverPlugin({
      updateDelay,
      editor,
      element,
      pluginKey: `${pluginKey}-hover`,
      shouldShow,
      tippyOptions,
      type: BaseHubInlineBlock.name,
      matchClasses: [
        BaseHubInlineBlock.options.HTMLAttributes.class,
        Link.options.HTMLAttributes.class,
      ],
    })

    editor.registerPlugin(hoverPlugin)

    return () => {
      editor.unregisterPlugin(`${pluginKey}-hover`)
    }
  }, [element, editor, updateDelay, shouldShow, tippyOptions, pluginKey])

  // Track selected instance
  React.useEffect(() => {
    const { $from } = editor.state.selection
    const basehubMark = $from
      .marks()
      .find((mark: Mark) => mark.type.name === BaseHubInlineBlock.name)

    setInstanceId(basehubMark?.attrs.id ?? null)

    return () => setInstanceId(null)
  }, [editor.state.selection])

  return (
    <div
      ref={setElement}
      className={clsx(
        'bg-dark-shade rounded border border-dark-border-solid p-1 text-dark-dim text-xs',
        className
      )}
    >
      <Tooltip delayDuration={200}>
        <div className="flex items-center">
          <span className="mx-1.5">
            {isWindows ? 'ctrl' : '⌘'} + click to open
          </span>
        </div>
      </Tooltip>
    </div>
  )
}
